import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import type { AttentionData } from './homeApi';
import { hasAttention } from './homeApi';
import { athensYmd, fmtDM, fmtTime, toAthensYmd, weekdayOf } from './format';
import { rich } from './ui';

/**
 * «Προσοχή»: ΜΟΝΟ όταν κάτι θέλει ενέργεια τώρα — ξεχασμένο Ζ (κουμπί «Κλείσιμο ταμείου» → το «Ταμείο»),
 * εκτυπωτές εκτός σύνδεσης (→ Εκτυπωτές), παραστατικά που δεν έφτασαν στην ΑΑΔΕ (→ Παραστατικά).
 * Πορτοκαλί/κόκκινο μόνο εδώ και στα Δ. 2+ ειδοποιήσεις: μία ανά γραμμή (το φύλλο είναι στενότερο από το
 * δείγμα — δύο σε μία γραμμή αναδιπλώνονταν με το διαχωριστικό στην αρχή της δεύτερης).
 */
export interface AttentionStripProps {
  attention: AttentionData | null;
  onCloseRegister?: () => void;
  onOpenPrinters?: () => void;
  onOpenFiscal?: () => void;
  now?: Date;
}

/** «12/10, 21:58» ή σκέτο «21:58» όταν είναι σήμερα. */
export function whenLabel(iso: string | null, now: Date): string {
  if (!iso) return '';
  const time = fmtTime(iso);
  const day = toAthensYmd(iso);
  if (!time || !day) return '';
  return day === athensYmd(now) ? time : `${fmtDM(day)}, ${time}`;
}

export const AttentionStrip: React.FC<AttentionStripProps> = ({ attention, onCloseRegister, onOpenPrinters, onOpenFiscal, now = new Date() }) => {
  const { t } = useTranslation(['home']);
  if (!attention || !hasAttention(attention)) return null;
  const items: Array<{ key: string; sev: 'w' | 'b'; text: React.ReactNode; action?: React.ReactNode }> = [];

  const { register, printers, fiscal_failed: fiscal } = attention;
  if (register.forgotten_z) {
    const day = toAthensYmd(register.open_since);
    const dayText = day ? `${t(`home:weekdayGen.${weekdayOf(day)}`)} ${fmtDM(day)}` : '';
    items.push({
      key: 'z',
      sev: 'b',
      text: rich(day ? t('home:attention.zForgotten', { day: dayText }) : t('home:attention.zForgottenNoDay')),
      action: onCloseRegister && (
        <button type="button" className="xh-link" onClick={onCloseRegister} data-testid="attention-close-register">
          {t('home:attention.closeRegister')}<ChevronRight aria-hidden="true" />
        </button>
      ),
    });
  }
  if (printers.offline.length > 0) {
    const one = printers.offline.length === 1 ? printers.offline[0] : null;
    const since = one ? whenLabel(one.last_seen, now) : '';
    const text = one
      ? (since ? t('home:attention.printerOfflineSince', { name: one.name, time: since }) : t('home:attention.printerOffline', { name: one.name }))
      : t('home:attention.printersOffline', { count: printers.offline.length, names: printers.offline.map(p => p.name).join(', ') });
    items.push({
      key: 'printers',
      sev: 'w',
      text: rich(text),
      action: onOpenPrinters && (
        <button type="button" className="xh-link" onClick={onOpenPrinters} data-testid="attention-printers">
          {t('home:attention.printers')}<ChevronRight aria-hidden="true" />
        </button>
      ),
    });
  }
  if (fiscal.count > 0) {
    const since = whenLabel(fiscal.oldest_at, now);
    items.push({
      key: 'fiscal',
      sev: 'b',
      text: rich(since
        ? t('home:attention.fiscalFailedSince', { count: fiscal.count, time: since })
        : t('home:attention.fiscalFailed', { count: fiscal.count })),
      action: onOpenFiscal && (
        <button type="button" className="xh-link" onClick={onOpenFiscal} data-testid="attention-fiscal">
          {t('home:attention.fiscal')}<ChevronRight aria-hidden="true" />
        </button>
      ),
    });
  }

  return (
    <div className={`xh-alerts${items.length > 1 ? ' stacked' : ''}`} role="status" aria-label={t('home:attention.title')} data-testid="attention-strip">
      <span className="xh-al-cap"><AlertTriangle aria-hidden="true" />{t('home:attention.title')}</span>
      <ul>
        {items.map(it => (
          <li key={it.key} data-testid={`attention-${it.key}-item`}>
            {/* η τελεία μέσα στο κείμενο: σε στενή οθόνη αναδιπλώνεται μαζί του, όχι μόνη της */}
            <span className="xh-al-text"><span className={`xh-sev ${it.sev}`} aria-hidden="true" />{it.text}</span>
            {it.action}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AttentionStrip;
